import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminClientUsersGetAdminClientsClientIdGuidUsersQueryKey,
  useAdminClientUsersGetAdminClientsClientIdGuidUsers,
  useAdminClientUsersPostAdminClientsClientIdGuidUsers,
} from '@/lib/api/admin-client-users/admin-client-users';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const InviteSchema = z.object({
  email: z.string().email().max(320),
  fullName: z.string().max(200).optional().or(z.literal('')),
  role: z.enum(['ClientViewer', 'ClientAdmin']),
});
type InviteForm = z.infer<typeof InviteSchema>;

interface Props {
  clientId: string | null;
  clientName?: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Admin-side portal-user management dialog. Exists purely to solve the
 * bootstrap chicken-and-egg: a fresh client has no ClientAdmin, so nobody can
 * invite one via the portal's self-serve flow. An admin opens this dialog
 * from the Clients list and seeds the first user.
 */
export function PortalUsersAdminDialog({ clientId, clientName, onOpenChange }: Props) {
  const open = clientId !== null;
  const queryClient = useQueryClient();
  const [invitePanel, setInvitePanel] = useState(false);

  const users = useAdminClientUsersGetAdminClientsClientIdGuidUsers(clientId ?? '', {
    query: { enabled: open },
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onOpenChange(false))}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Portal users{clientName ? ` — ${clientName}` : ''}</DialogTitle>
          <DialogDescription>
            Seed portal users for this client. The first ClientAdmin must be invited here; after that they can invite
            more from the portal themselves.
          </DialogDescription>
        </DialogHeader>

        {invitePanel && clientId ? (
          <InvitePanel
            clientId={clientId}
            onDone={async () => {
              setInvitePanel(false);
              await queryClient.invalidateQueries({
                queryKey: getAdminClientUsersGetAdminClientsClientIdGuidUsersQueryKey(clientId),
              });
            }}
            onCancel={() => setInvitePanel(false)}
          />
        ) : (
          <>
            <div className="space-y-2">
              {users.isLoading ? (
                <div className="h-10 animate-pulse rounded-md bg-muted" />
              ) : !users.data || users.data.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No portal users yet. Invite the first ClientAdmin to unlock the self-serve portal for this partner.
                </div>
              ) : (
                <div className="max-h-80 space-y-1 overflow-auto">
                  {users.data.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between rounded-md border border-border p-2 text-sm"
                    >
                      <div>
                        <div className="font-medium">{u.fullName || u.email}</div>
                        <div className="font-mono text-xs text-muted-foreground">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={u.role === 'ClientAdmin' ? 'default' : 'secondary'}>{u.role}</Badge>
                        {u.isActive ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="warning">Disabled</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button type="button" onClick={() => setInvitePanel(true)} className="gap-2">
                <Plus className="h-4 w-4" /> Invite user
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InvitePanel({
  clientId,
  onDone,
  onCancel,
}: {
  clientId: string;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [tempPw, setTempPw] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);

  const mutation = useAdminClientUsersPostAdminClientsClientIdGuidUsers({
    mutation: {
      onSuccess: (result) => {
        setTempPw(result.temporaryPassword);
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteForm>({
    resolver: zodResolver(InviteSchema),
    defaultValues: { email: '', fullName: '', role: 'ClientAdmin' },
  });

  const submit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({
        clientId,
        data: { email: values.email, fullName: values.fullName || null, role: values.role },
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? 'That email already exists at this client.' : 'Could not invite user.');
    }
  });

  const copy = async () => {
    if (!tempPw) return;
    await navigator.clipboard.writeText(tempPw);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!tempPw) {
    return (
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="space-y-2">
          <Label htmlFor="invite-email">Email</Label>
          <Input id="invite-email" type="email" autoComplete="off" {...register('email')} />
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-fullName">Full name (optional)</Label>
          <Input id="invite-fullName" {...register('fullName')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-role">Role</Label>
          <select
            id="invite-role"
            {...register('role')}
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="ClientAdmin">ClientAdmin (can invite more users)</option>
            <option value="ClientViewer">ClientViewer (read-only)</option>
          </select>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Back
          </Button>
          <Button type="submit" disabled={isSubmitting || mutation.isPending}>
            {mutation.isPending ? 'Inviting…' : 'Invite user'}
          </Button>
        </DialogFooter>
      </form>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-200">
        Save this password — it won't be shown again. Share it with the invitee over a secure channel.
      </div>
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted p-3">
        <code className="break-all font-mono text-xs">{visible ? tempPw : '•'.repeat(tempPw.length)}</code>
        <div className="flex gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={visible ? 'Hide' : 'Show'}
            onClick={() => setVisible((v) => !v)}
          >
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button type="button" size="icon" variant="ghost" aria-label="Copy" onClick={copy}>
            {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        I've delivered the password to the invitee.
      </label>
      <DialogFooter>
        <Button
          type="button"
          disabled={!ack}
          onClick={async () => {
            setTempPw(null);
            setVisible(false);
            setCopied(false);
            setAck(false);
            reset();
            await onDone();
          }}
        >
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}
